import {
  AwaitContainer,
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Search,
} from '@ibsys/kit';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NestedItemCard } from './NestedItemCard';

type Labels = {
  dialogTitle: string;
  searchPlaceholder?: string;
  noItemsFound?: string;
  cancel?: string;
  add?: string;
};

type AddNestedItemsDialogProps<T> = {
  onOpenChange: (open: boolean) => void;
  availableItems: T[];
  onSave: (items: T[]) => void;
  labels: Labels;
  renderTitle: (item: T) => ReactNode;
  renderDescription?: (item: T) => ReactNode;
  getItemId: (item: T) => string;
  searchFilter?: (item: T, searchQuery: string) => boolean;
};

export const AddNestedItemsDialog = <T,>({
  onOpenChange,
  availableItems,
  onSave,
  labels,
  renderTitle,
  renderDescription,
  getItemId,
  searchFilter,
}: AddNestedItemsDialogProps<T>) => {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return availableItems;
    if (searchFilter) {
      return availableItems.filter((item) => searchFilter(item, search));
    }
    return availableItems;
  }, [availableItems, search, searchFilter]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleSave = () => {
    // Поиск только сужает показ: отмеченное под прежним запросом добавляется вместе с видимым сейчас
    const newItems = availableItems.filter((item) => selectedIds.includes(getItemId(item)));
    onSave(newItems);
    handleCancel();
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedIds([]);
    setSearch('');
  };

  return (
    <DialogContent className="max-w-160">
      <DialogHeader>
        <DialogTitle>{labels.dialogTitle}</DialogTitle>
      </DialogHeader>
      <div className="flex min-w-0 flex-col gap-4">
        {/* Search отдаёт onChange только по уходу фокуса, Enter и очистке, а список окна должен сужаться
            по мере набора: иначе первый щелчок после набора перестраивает список под курсором и теряется */}
        <Search
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={labels.searchPlaceholder || t('search')}
        />

        <AwaitContainer isLoading={false}>
          <div className="flex flex-col gap-3 max-h-120 overflow-auto">
            {filteredItems.map((item) => {
              const id = getItemId(item);
              return (
                <NestedItemCard
                  key={id}
                  item={item}
                  renderTitle={renderTitle}
                  renderDescription={renderDescription}
                  action={<Checkbox value={selectedIds.includes(id)} onChange={() => handleToggle(id)} />}
                />
              );
            })}
            {filteredItems.length === 0 && (
              <div className="text-muted-foreground">{labels.noItemsFound || t('noItemsFound')}</div>
            )}
          </div>
        </AwaitContainer>
      </div>

      <DialogFooter>
        <div className="flex gap-3 justify-end w-full">
          <Button variant="ghost" onClick={handleCancel}>
            {labels.cancel || t('cancel')}
          </Button>
          <Button onClick={handleSave} disabled={selectedIds.length === 0}>
            {labels.add || t('add')}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
